import { z } from 'zod'
import * as React from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Button as NextUIButton, cn, Spinner, useDisclosure } from '@nextui-org/react'
import { Button } from '@/components/ui/button'
import { Icon } from '@iconify/react'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { schema } from '@/pages/powerdoc-project-view'
import DragHandle from '@/pages/powerdoc-project-view/components/DragHandle'
import DraggableRow from '@/pages/powerdoc-project-view/components/DraggableRow'
import { TableCellViewer } from '@/pages/powerdoc-project-view/components/TableCellViewer'
import { CalendarIcon, DownloadIcon, EyeIcon, LayoutTemplateIcon, PlusIcon, SendIcon } from 'lucide-react'
import { Calendar } from 'src/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format } from 'date-fns'
import Select, { components } from 'react-select'
import { useMemo, useState } from 'react'
import { useSocketContextData } from '@/context/socket'
import UserCell from '@/pages/team/user-cell'
import { PowerDocsEventsName } from '@/types/SocketEvents'
import dayjs from 'dayjs'
import DocumentPreview from '@/pages/powerdoc-view/DocumentPreview'
import { baseURL } from '@/lib/api'
import { useParams } from 'react-router-dom'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from 'src/components/ui/dropdown-menu'
import InviteTeamMember from './InviteTeamMember'

import toast from 'react-hot-toast'
import Mixpanel from 'src/lib/mixpanel'
import { useWorkspaceContextData } from 'src/context/workspace'

const progressList = [
  { key: 'NOT_STARTED', label: 'Not started', icon: <Icon icon="lucide:circle-alert" width="15" /> },
  { key: 'IN_PROGRESS', label: 'In progress', icon: <Icon icon="lucide:loader-2" width="15" /> },
  { key: 'OVERDUE', label: 'Overdue', icon: <Icon icon="lucide:alarm-clock" width="15" /> },
  { key: 'NEEDS_REVIEW', label: 'Needs review', icon: <Icon icon="lucide:file-search" width="15" /> },
  { key: 'SUBMITTED_FOR_REVIEW', label: 'Submitted for review', icon: <Icon icon="lucide:send" width="15" /> },
  { key: 'CHANGE_REQUESTED', label: 'Changes requested', icon: <Icon icon="lucide:edit-3" width="15" /> },
  { key: 'APPROVED', label: 'Approved', icon: <Icon icon="lucide:check-circle" width="15" /> },
]

export default function DataTable({
  data: initialData,
  members,
  isLoading,
  variant,
  onCreateVariantClick,
  isGenerating,
  addSectionClicked,
  setDoc,
  isTemplate,
  loadMembers,
}: {
  data: z.infer<typeof schema>[]
  members: {
    id: number
    firstname: string
    lastname: string
    email: string
  }[]
  isLoading: boolean
  variant: string
  onCreateVariantClick: () => void
  isGenerating: boolean
  isTemplate: boolean
  addSectionClicked: () => void
  setDoc: (value: any) => void
  loadMembers: (activeWorkspace: string) => void
}) {
  const { io } = useSocketContextData()
  const [url, setUrl] = useState<string>('')
  const { isOpen: isTeamInviteOpen, onOpen: onTeamInviteOpen, onClose: onTeamInviteClose } = useDisclosure()
  const columns: ColumnDef<z.infer<typeof schema>>[] = useMemo(
    () => [
      {
        id: 'drag',
        header: () => null,
        cell: ({ row }) => <DragHandle id={row.original.id} />,
        enableSorting: false,
      },
      {
        id: 'select',
        header: () => null,
        cell: ({ row }) => <div className="flex items-center justify-center"></div>,
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: 'title',
        header: 'Section',
        cell: ({ row }) => {
          return <TableCellViewer item={row.original} />
        },
        enableHiding: false,
        enableSorting: false,
      },
      {
        accessorKey: 'progress',
        header: 'Progress',
        cell: ({ row }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [progress, setProgress] = useState<string>(row.original.progress)
          const updateStatus = (value: string) => {
            setProgress(value)
            io.emit(PowerDocsEventsName.UPDATE_SECTION_PROGRESS, {
              progress: value,
              token: localStorage.getItem('accessToken'),
              sectionId: row.original.id,
            })
            setDoc((prev: any) => {
              let newDoc
              if (variant.toLowerCase() === 'custom') {
                newDoc = {
                  ...prev,
                  customDocs: prev.customDocs.map((item: any) => {
                    if (item.uuid === params.customDocUUID) {
                      return {
                        ...item,
                        progress: value,
                      }
                    }
                    return item
                  }),
                }
              } else {
                newDoc = {
                  ...prev,
                  [variant.toLowerCase()]: {
                    ...(prev[variant.toLocaleLowerCase()] ?? {}),
                    sections: (prev[variant.toLocaleLowerCase()].sections ?? []).map((item) => ({
                      ...item,
                      progress: item.id === row.original.id ? value : item.progress,
                    })),
                  },
                }
              }
              return newDoc
            })
          }
          const currentProgress = progressList.filter((item) => item.key === progress)?.pop() || progressList[0]
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild className={'focus:outline-none'}>
                <Button
                  variant="outline"
                  className="focus:none data-[state=open]:bg-muted text-muted-foreground flex size-8 rounded-xl gap-2"
                  size="sm"
                >
                  {currentProgress.icon} {currentProgress.label}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-fit bg-white cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                {progressList.map((item) => (
                  <DropdownMenuItem
                    key={item.key}
                    onSelect={() => {
                      updateStatus(item.key)
                    }}
                    className="cursor-pointer"
                  >
                    {item.icon} {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: 'dueDate',
        header: () => <div className="w-full text-left">Due date</div>,
        cell: ({ row }) => {
          const [dueDate, setDueDate] = useState<string>(row.original.dueDate)
          const [calendarOpen, setCalendarOpen] = useState(false)
          const { io } = useSocketContextData()
          const updateDueDate = (value: Date) => {
            setDueDate(value as unknown as string)
            setCalendarOpen(false)
            io.emit(PowerDocsEventsName.UPDATE_SECTION_DUE_DATE, {
              dueDate: value,
              sectionId: row.original.id,
              token: localStorage.getItem('accessToken'),
            })
          }
          return (
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={'outline'}
                  className={cn(
                    'w-[120] justify-start text-left font-normal items-center text-sm align-middle border-none shadow-none box-shadow-none',
                    !dueDate && 'text-default-500',
                    dueDate && dayjs().isAfter(dayjs(dueDate)) && 'border-danger',
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCalendarOpen((prev) => !prev)
                  }}
                >
                  <CalendarIcon size={16} />
                  {dueDate ? format(dueDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start" onClick={(e) => e.stopPropagation()}>
                <Calendar
                  initialFocus
                  mode="single"
                  selected={dueDate as unknown as Date}
                  //@ts-ignore
                  onSelect={updateDueDate}
                />
              </PopoverContent>
            </Popover>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: 'lastEdit',
        header: () => <div className="w-full text-left">Last edit</div>,
        cell: ({ row }) => (
          <Button
            variant={'outline'}
            className={cn(
              'w-[120] justify-start text-left font-normal items-center text-sm align-middle border-none shadow-none',
              !row.original.editDate && 'text-default-500',
            )}
          >
            <CalendarIcon size={16} />
            {row.original.editDate ? format(row.original.editDate, 'PPP') : <span>No Edit yet</span>}
          </Button>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'responsible',
        header: 'Responsible',
        cell: ({ row }) => {
          const [selectedMember, setSelectedMember] = useState<any>(
            row.original.permissions?.[0]?.user
              ? {
                  value: row.original.permissions?.[0]?.user?.email,
                  label: (
                    <h1>
                      {[
                        row.original.permissions?.[0]?.user?.firstname,
                        row.original.permissions?.[0]?.user?.lastname,
                      ].join(' ')}
                    </h1>
                  ),
                }
              : {},
          )
          const [loading, setLoading] = useState<boolean>(false)
          const asignSectionToMember = async (email: string) => {
            setLoading(true)
            await io.emitWithAck(PowerDocsEventsName.ASSIGN_TO_MEMBER, {
              sectionId: row.original.id,
              email,
              token: localStorage.getItem('accessToken'),
            })
            setLoading(false)
          }
          const options = members.map((item: any) => ({
            value: item?.email,
            label: `${item?.firstname} ${item?.lastname}`,
          }))
          const SelectMenuButton = (props) => {
            return (
              <components.MenuList {...props}>
                {props.children}
                <div className="p-2 cursor-pointer">
                  <button
                    className="justify-center w-full self-center bg-secondary p-2 rounded-md"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation() // prevent react-select from hijacking the event
                      e.preventDefault() // prevent default dropdown behavior
                      onTeamInviteOpen()
                    }}
                  >
                    <span className="text-sm text-white cursor-pointer">Invite team member</span>
                  </button>
                </div>
              </components.MenuList>
            )
          }
          return (
            <div className={'bg-white h-[40]'} onClick={(e) => e.stopPropagation()}>
              <Select
                options={options}
                isDisabled={loading}
                placeholder="Assign to team member"
                value={selectedMember}
                onChange={(data) => {
                  console.log(data)
                  setSelectedMember(data)
                  asignSectionToMember(data.value)
                }}
                formatCreateLabel={(inputValue: string) => `Create new client: ${inputValue}`}
                menuPosition="fixed"
                menuPortalTarget={document.body}
                formatOptionLabel={(option, { context }) => {
                  return context === 'menu' ? (
                    <>
                      <UserCell email={option.email} avatar="img/icon.png" name={option.label} permission={''} />
                    </>
                  ) : (
                    option.label
                  )
                }}
                components={{
                  MenuList: SelectMenuButton,
                }}
                styles={{
                  container: (baseStyles) => ({
                    ...baseStyles,
                    minWidth: 120,
                    padding: 0,
                    height: '100%',
                    boxShadow: 'none',
                    borderColor: '#e5e7eb',
                    borderWidth: 2,
                    borderRadius: '20px',
                  }),
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: 'none',
                    height: '100%',
                    width: '100%',
                    outline: 'none',
                    boxShadow: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    borderRadius: '20px',
                  }),
                  menuPortal: (defaultStyles) => ({
                    ...defaultStyles,
                    zIndex: 99999,
                  }),
                  option: (styles, state) => ({
                    ...styles,
                    cursor: 'pointer',
                    fontSize: '10px',
                  }),
                }}
              />
            </div>
          )
        },
        enableSorting: false,
      },
    ],
    [members],
  )

  const [data, setData] = React.useState(() => initialData)

  const [rowSelection, setRowSelection] = React.useState({})
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [sorting, setSorting] = React.useState<SortingState>([])

  const sortableId = React.useId()
  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  const dataIds = React.useMemo<UniqueIdentifier[]>(() => data?.map(({ id }) => id) || [], [data, initialData])
  const inActiveButtonClass = 'bg-transparent text-default-400 pl-5 pr-5'
  const activeButtonClass = 'text-black-500 font-medium bg-white rounded-md  pl-5 pr-5'

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    getRowId: (row) => row.id.toString(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  })

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (active && over && active.id !== over.id) {
      const oldIndex = dataIds.indexOf(active.id)
      const newIndex = dataIds.indexOf(over.id)
      io.emit(PowerDocsEventsName.REORDER_POWERDOC_SECTIONS, {
        variant,
        oldIndex,
        newIndex,
        dragged: active.id,
        dropped: over.id,
        documentUUID: params.documentUUID,
        token: localStorage.getItem('accessToken'),
      })
      setData((data) => {
        const oldIndex = dataIds.indexOf(active.id)
        const newIndex = dataIds.indexOf(over.id)
        return arrayMove(data, oldIndex, newIndex)
      })
    }
  }
  const params = useParams()
  const [isPreview, setIsPreview] = useState<boolean>(false)
  const downloadPdf = () => {
    const popup = window.open(
      `${baseURL}/powerdocs/export/${
        variant.toLocaleLowerCase() === 'custom' ? params.customDocUUID : params.documentUUID
      }/${variant}`,
    )
    if (popup) {
      popup?.focus()
      popup.addEventListener('unload', () => {
        popup.close()
      })
    }
  }

  const share = (variant: string) => {
    try {
      const documentUUID = variant.toLowerCase() === 'custom' ? params.customDocUUID : params.documentUUID
      const url = `${document.location.origin}/${variant.toLowerCase()}/${documentUUID}`
      navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')

      Mixpanel.track('Share link copied', {
        location: 'Document Overview',
        document_id: documentUUID,
        link: url,
        variant,
      })
    } catch (error) {
      toast.error('Failed to copy the link. Please try again.')
    }
  }
  const { activeWorkspace, loadTemplates } = useWorkspaceContextData()
  const saveTemplate = async () => {
    const documentUUID = variant.toLowerCase() === 'custom' ? params.customDocUUID : params.documentUUID
    await io.emitWithAck(PowerDocsEventsName.SAVE_TEMPLATE, {
      documentUUID,
      variant,
      token: localStorage.getItem('accessToken'),
    })
    setDoc((prev: any) => {
      if (variant.toLowerCase() === 'custom') {
        return {
          ...prev,
          customDocs: prev.customDocs.map((item: any) => {
            if (item.uuid === params.customDocUUID) {
              return {
                ...item,
                isTemplate: !item.isTemplate,
              }
            }
            return item
          }),
        }
      } else {
        return {
          ...prev,
          [variant.toLowerCase()]: {
            ...(prev[variant.toLocaleLowerCase()] ?? {}),
            isTemplate: !prev[variant.toLocaleLowerCase()]?.isTemplate,
          },
        } as any
      }
    })
    loadTemplates?.(activeWorkspace)
  }
  return (
    <Tabs defaultValue="outline" className="w-full flex-col justify-start gap-6">
      <div className="flex px-4 lg:px-6 mt-8 items-end">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1 text-2xl font-bold capitalize">
            {variant.toLowerCase() === 'proposal' && <Icon icon="lucide:file-text" width="18" />}
            {variant.toLowerCase() === 'quotation' && <Icon icon="lucide:file-check" width="18" />}
            {variant.toLowerCase() === 'offer' && <Icon icon="lucide:file-spreadsheet" width="18" />}
            {variant}
          </div>
          <div className="flex gap-2">
            <div className="flex justify-center items-center text-sm uppercase font-semibold text-secondary">
              <Icon icon="lucide:lightbulb" className="text-secondary" />
              Tip
            </div>
            <span className="text-sm">
              Below is the outline of your document. Modify directly here, or click a section title to edit.
            </span>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className={'rounded-3xl'} onClick={() => saveTemplate()}>
            <LayoutTemplateIcon size={16} />
            <span className="hidden lg:inline">{isTemplate ? 'Remove Template' : 'Save as Template'}</span>
          </Button>
          <Button variant="outline" size="sm" className={'rounded-3xl'} onClick={() => setIsPreview(true)}>
            <EyeIcon size={16} />
            <span className="hidden lg:inline">Preview</span>
          </Button>
          <Button variant="outline" size="sm" className={'rounded-3xl'} onClick={() => share(variant)}>
            <SendIcon size={16} />
            <span className="hidden lg:inline">Share</span>
          </Button>
          <Button variant="outline" size="sm" className={'rounded-3xl'} onClick={downloadPdf}>
            <DownloadIcon size={16} />
            <span className="hidden lg:inline">Download</span>
          </Button>
        </div>
      </div>
      {isPreview && (
        <div className={'flex absolute top-0 left-0 w-full h-full z-50 bg-zinc-800'}>
          <div className="flex w-full h-full">
            <DocumentPreview
              url={`${baseURL}/powerdocs/preview/${
                variant === 'custom' ? params.customDocUUID : params.documentUUID
              }/${variant}`}
              close={() => setIsPreview(false)}
            />
          </div>
        </div>
      )}
      <TabsContent value="outline" className="relative flex flex-col gap-4 overflow-auto px-4 lg:px-6">
        <div className="overflow-hidden rounded-lg">
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
            id={sortableId}
          >
            <Table className="border-0">
              <TableHeader className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className={'!border-b-0'}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead
                          key={header.id}
                          colSpan={header.colSpan}
                          className={`font-regular text-xs text-[#09090B] bg-[#C3E3E8] ${
                            index === 0 ? 'rounded-tl-xl rounded-bl-xl' : ''
                          } ${index === headerGroup.headers.length - 1 ? 'rounded-tr-xl rounded-br-xl' : ''}`}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className="**:data-[slot=table-cell]:first:w-8">
                {table.getRowModel().rows?.length ? (
                  <>
                    <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                      {table.getRowModel().rows.map((row) => (
                        <DraggableRow key={row.id} row={row} />
                      ))}
                    </SortableContext>
                    {isGenerating && (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                          <div className={'items-center gap-2 flex-row flex justify-center'}>
                            <Spinner size={'sm'} />
                            <span>Generating {variant}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ) : isGenerating ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      <div className={'items-center gap-2 flex-row flex justify-center'}>
                        <Spinner size={'sm'} />
                        <span>Generating {variant}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      {isLoading ? (
                        <div className={'items-center gap-2 flex-row flex justify-center'}>
                          <Spinner size={'sm'} />
                          <span>Loading sections</span>
                        </div>
                      ) : (
                        <div className={'items-center gap-4 pt-4 flex-col flex justify-center'}>
                          <span className={'text-lg font-medium text-default-300 mt-10'}>No section created yet</span>
                          <NextUIButton color={'secondary'} onPress={onCreateVariantClick} isDisabled={isGenerating}>
                            <PlusIcon />
                            Generate {variant.toLowerCase()} now
                          </NextUIButton>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </DndContext>
        </div>
        <div className="flex items-center justify-between px-4 mb-20">
          <div className="text-muted-foreground hidden flex-1 text-sm lg:flex">
            <div className={'flex flex-row gap-1 items-center cursor-pointer'} onClick={addSectionClicked}>
              <Icon icon="lucide:plus" width="20" />
              <span className={'text-medium text-[#3F3F46] font-medium'}>Add section</span>
            </div>
          </div>
          <div className="flex w-full items-center gap-8 lg:w-fit"></div>
        </div>
      </TabsContent>
      <TabsContent value="past-performance" className="flex flex-col px-4 lg:px-6">
        <div className="aspect-video w-full flex-1 rounded-lg border border-dashed"></div>
      </TabsContent>
      <TabsContent value="key-personnel" className="flex flex-col px-4 lg:px-6">
        <div className="aspect-video w-full flex-1 rounded-lg border border-dashed"></div>
      </TabsContent>
      <InviteTeamMember isOpen={isTeamInviteOpen} onClose={onTeamInviteClose} loadMembers={loadMembers} />
    </Tabs>
  )
}
